import _ from "lodash";
import React, { useState } from "react";
import toast from "react-hot-toast";
import useMessageRow from "../../../hooks/useMessageRow";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Dropdown, Modal } from "react-bootstrap";
import { MessagePriorities } from "../../clientmessages/hooks/useAllMessages";
import useOpenDocument from "../../../hooks/useOpenDocument";
import { Button } from "../../../components";
import { DateInput } from "../../../components/forms";
import ThemedModal from "../../../components/utilities/ThemedModal";
import { GridMessageType } from "../../../types/MessageType";
import { usePersonalMessageThreadController } from "../hook/PersonalMessageThreadControllerContext";

const PersonalMessagesVirtualTableRowActionColToggle = React.forwardRef<HTMLDivElement>((props, ref) => (
    <div ref={ref} {...props}>
        <FontAwesomeIcon className="has-pointer" icon={['fas', 'ellipsis']} />
    </div>
));

const OpenReportActionMenuItem = ({ reportDocumentId }) => {
    const { openDocument } = useOpenDocument();
    return (
        <Dropdown.Item onClick={() => openDocument(reportDocumentId, 'Message Report')}>
            Open Report
        </Dropdown.Item>
    );
};

const ReadMessageActionMenuItem = ({ label, readOptions, showCheck, read, unread, unreadOptions }) => {
    const onHandleClick = (e) => {
        if (typeof (e?.preventDefault) === "function") {
            e.preventDefault();
        }

        if (!showCheck && (!read || typeof (read) !== 'function')) {
            return;
        }

        if (showCheck && (!unread || typeof (unread) !== 'function')) {
            return;
        }

        showCheck ? unread(unreadOptions) : read(readOptions);
    };

    return <Dropdown.Item className="d-flex justify-content-between align-items-center" onClick={onHandleClick}>
        {label}
        {showCheck && <FontAwesomeIcon
            icon="check"
            className="text-success ms-2"
        />}
    </Dropdown.Item>;
};

const UpdateReminderDateActionMenuItem = ({ userReminderDate, updateRecipient }) => {

    const [showModal, setShowModal] = useState(false);

    return <React.Fragment>
        <Dropdown.Item onClick={() => setShowModal(true)}>Set Reminder Date</Dropdown.Item>
        <Modal show={showModal} centered backdrop="static">
            <ThemedModal.Header>
                <Modal.Title>Update Reminder Date</Modal.Title>
            </ThemedModal.Header>
            <Modal.Body>
                <div className="row px-4">
                    <DateInput
                        value={userReminderDate}
                        isClearable
                        onBlur={(value) => updateRecipient("reminderDate", value)}
                        label="Remind On"
                    />
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button onClick={() => setShowModal(false)} variant="light">Close</Button>
            </Modal.Footer>
        </Modal>
    </React.Fragment>
};

const UserPriorityMessageActionMenuItem = ({ isCurrent, label, priority, updateRecipient }) => {
    const onHandleClick = () => {
        if (!updateRecipient || typeof (updateRecipient) !== 'function') {
            return;
        }

        updateRecipient("recipientPriority", priority)
            .then(
                _ => toast.success("Priority updated."),
                error => toast.error(error?.data)
            );
    };

    return (
        <Dropdown.Item className="d-flex justify-content-between align-items-center" onClick={onHandleClick}>
            {label}
            {isCurrent && <FontAwesomeIcon
                icon="check"
                className="text-success ms-2"
            />}
        </Dropdown.Item>
    );
};

const ViewPersonalMessageThreadActionMenuItem = ({ threadId, setSelectedThreadId }) => {
    const onHandleClick = () => {
        if (!setSelectedThreadId || typeof (setSelectedThreadId) !== 'function') {
            return;
        }
        setSelectedThreadId(threadId);
    };

    return (
        <Dropdown.Item onClick={onHandleClick}>
            Open Conversation
        </Dropdown.Item>
    );
};

export type TableRowActionColumnProps = {
    data: GridMessageType
    isRecipient: boolean
    userId: string
}

const TableRowActionColumn = ({ data, isRecipient, userId } : Readonly<TableRowActionColumnProps>) => {
    const menuId = React.useId();
    const toggleId = React.useId();
    const {
        isActionRequired,
        isDone,
        messageId,
        threadId,
        userMessagePriority,
        userNoFurtherActionTime,
        userReadTime,
        reportDocumentId,
        userReminderDate
    } = data || {};

    const { setThread } = usePersonalMessageThreadController()!;
    const { readUserMessage, unreadUserMessage, updateRecipient } = useMessageRow(userId, messageId);
    return (
        <Dropdown id={menuId} className="caret-off w-100">
            <Dropdown.Toggle as={PersonalMessagesVirtualTableRowActionColToggle} id={toggleId} />
            <Dropdown.Menu>
                <ViewPersonalMessageThreadActionMenuItem
                    threadId={threadId}
                    setSelectedThreadId={() => setThread(threadId)}
                />
                {reportDocumentId && <OpenReportActionMenuItem reportDocumentId={reportDocumentId} />}
                {isRecipient && <React.Fragment>
                    <Dropdown.Divider />
                    <Dropdown.Header>Actions</Dropdown.Header>
                    {isActionRequired && <ReadMessageActionMenuItem
                        showCheck={isDone === true}
                        read={readUserMessage}
                        unread={unreadUserMessage}
                        readOptions={{ isDone: true, noFurtherActionRequired: true }}
                        unreadOptions={{ undoRead: false, undoDone: true, undoNoFurtherActionRequired: false }}
                        label={"Task Complete, no further action required"}
                    />}
                    <ReadMessageActionMenuItem
                        showCheck={userNoFurtherActionTime}
                        read={readUserMessage}
                        unread={unreadUserMessage}
                        readOptions={{ isDone: false, noFurtherActionRequired: true }}
                        unreadOptions={{ undoRead: false, undoDone: true, undoNoFurtherActionRequired: true }}
                        label={"No further action required"}
                    />
                    <UpdateReminderDateActionMenuItem
                        userReminderDate={userReminderDate}
                        updateRecipient={updateRecipient}
                    />
                    <Dropdown.Divider />
                    <Dropdown.Header>Priority</Dropdown.Header>
                    <UserPriorityMessageActionMenuItem
                        label="Low"
                        priority={MessagePriorities.Low}
                        isCurrent={userMessagePriority === MessagePriorities.Low}
                        updateRecipient={updateRecipient}
                    />
                    <UserPriorityMessageActionMenuItem
                        label="Medium"
                        priority={MessagePriorities.Medium}
                        isCurrent={userMessagePriority === MessagePriorities.Medium}
                        updateRecipient={updateRecipient}
                    />
                    <UserPriorityMessageActionMenuItem
                        label="High"
                        priority={MessagePriorities.High}
                        isCurrent={userMessagePriority === MessagePriorities.High}
                        updateRecipient={updateRecipient}
                    />
                </React.Fragment>}
            </Dropdown.Menu>
        </Dropdown>
    );

};

export default TableRowActionColumn;